import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent, ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { fetchConfig } from '@theme/utilities';

/**
 * Cross-sell widgets that replace the Selleasy app.
 *
 * - Add buttons of cross-sell cards (snippets/fabini-cross-sell-card.liquid) and product page
 *   bundles (`data-fabini-ajax-add`) are plain forms posting to /cart/add, enhanced here into a
 *   single AJAX request that also returns the cart sections, so the cart drawer opens instead
 *   of the cart page. Adds inside a cross-sell are reported by assets/fabini-custom-events.js.
 * - <fabini-product-cross-sell> loads Shopify recommendations on the product page.
 * - <fabini-bundle-slot> re-renders the product page bundle tile when a bundle enters or
 *   leaves the cart, so bundles in the cart give way to the cross-sell.
 * - The cart drawer cross-sell is rendered by Liquid and uses the cart-recommended-slider
 *   element from assets/cart-drawer.js.
 */

const FORM_SELECTOR = 'form[data-fabini-cross-sell-form], form[data-fabini-ajax-add]';

/** @type {Promise<Set<string>> | null} */
let cartProductIdsRequest = null;

// Capture runs before the components' own cart update listeners, so they all read the new cart.
document.addEventListener(
  ThemeEvents.cartUpdate,
  () => {
    cartProductIdsRequest = null;
  },
  { capture: true }
);

/**
 * Product ids in the cart, fetched once per cart update for all cross-sell components.
 * @returns {Promise<Set<string>>}
 */
function getCartProductIds() {
  cartProductIdsRequest ??= fetch(`${Theme.routes.cart_url}.js`)
    .then((response) => (response.ok ? response.json() : { items: [] }))
    .then((cart) => new Set((cart.items ?? []).map((/** @type {{ product_id: number }} */ item) => String(item.product_id))))
    .catch(() => new Set());

  return cartProductIdsRequest;
}

/**
 * Variants with an add in flight. Kept here rather than in the DOM: cart re-renders morph
 * the forms and would reset a busy attribute while the request is still running.
 * @type {Set<string>}
 */
const pendingVariantIds = new Set();

/**
 * Adds the card's variant to the cart. Cart components re-render from the sections
 * returned by the same request, which also removes the product from cart offers.
 * @param {HTMLFormElement} form
 */
async function addToCart(form) {
  const formData = new FormData(form);
  const variantId = String(formData.get('id'));
  if (pendingVariantIds.has(variantId)) return;
  pendingVariantIds.add(variantId);
  form.setAttribute('aria-busy', 'true');

  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  const { productId } = form.dataset;

  document.dispatchEvent(new CustomEvent('cart:add-start'));

  try {
    const response = await fetch(Theme.routes.cart_add_url, fetchConfig('javascript', { body: formData }));
    const data = await response.json();
    // Events bubble to the cart components from the form; if a re-render removed it meanwhile,
    // they go to the document so the cart still updates (without cross-sell attribution).
    const target = form.isConnected ? form : document;

    if (data.status) {
      target.dispatchEvent(new CartErrorEvent(variantId, data.message, data.description, data.errors));
      return;
    }

    // The cart icon adds `itemCount` to its current count for product form sources,
    // which is exactly what these forms do (quantity 1).
    target.dispatchEvent(
      new CartAddEvent({}, variantId, {
        source: 'product-form-component',
        itemCount: 1,
        productId,
        sections: data.sections,
        items: [data],
      })
    );
  } catch (error) {
    console.error(error);
  } finally {
    pendingVariantIds.delete(variantId);
    form.removeAttribute('aria-busy');
  }
}

document.addEventListener('submit', (event) => {
  const form = event.target;
  // `Theme` is a global `const` from snippets/scripts.liquid, not a `window` property.
  if (!(form instanceof HTMLFormElement) || !form.matches(FORM_SELECTOR) || typeof Theme === 'undefined') {
    return;
  }

  event.preventDefault();
  addToCart(form);
});

/**
 * Recommendation responses per URL, shared by all instances (variant re-renders reconnect the element).
 * @type {Map<string, Promise<HTMLElement[]>>}
 */
const candidateRequests = new Map();

/**
 * Product page cross-sell: the first `limit` recommended products of the primary intent that
 * are not in the cart; the fallback intent is used only when the primary intent leaves none.
 *
 * @typedef {{ list: HTMLUListElement }} ProductCrossSellRefs
 * @extends {Component<ProductCrossSellRefs>}
 */
class FabiniProductCrossSell extends Component {
  requiredRefs = ['list'];

  #renderId = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  #handleCartUpdate = () => {
    this.#render();
  };

  async #render() {
    const renderId = ++this.#renderId;
    const intent = this.dataset.intent || 'complementary';
    const fallbackIntent = this.dataset.fallbackIntent || 'none';
    const limit = Number(this.dataset.limit) || 2;
    const [cartProductIds, candidates] = await Promise.all([getCartProductIds(), this.#loadCandidates(intent)]);

    let cards = this.#pick(candidates, cartProductIds, limit);
    if (cards.length === 0 && fallbackIntent !== 'none' && fallbackIntent !== intent) {
      cards = this.#pick(await this.#loadCandidates(fallbackIntent), cartProductIds, limit);
    }

    if (renderId === this.#renderId) this.#show(cards);
  }

  /**
   * @param {HTMLElement[]} candidates
   * @param {Set<string>} cartProductIds
   * @param {number} limit
   */
  #pick(candidates, cartProductIds, limit) {
    return candidates.filter((card) => !cartProductIds.has(card.dataset.productId ?? '')).slice(0, limit);
  }

  /**
   * @param {string} intent
   * @returns {Promise<HTMLElement[]>}
   */
  #loadCandidates(intent) {
    const { url, productId, sectionId } = this.dataset;
    const params = new URLSearchParams({
      product_id: productId ?? '',
      intent,
      limit: '10',
      section_id: sectionId ?? '',
    });
    const requestUrl = `${url}?${params}`;

    let request = candidateRequests.get(requestUrl);
    if (!request) {
      request = fetch(requestUrl)
        .then((response) => {
          if (!response.ok) throw new Error(`Recommendations responded ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          return Array.from(doc.querySelectorAll('[data-fabini-cross-sell-candidates] > [data-product-id]')).filter(
            (card) => card instanceof HTMLElement
          );
        })
        .catch(() => {
          // Retry on the next render instead of hiding the offer until a reload.
          candidateRequests.delete(requestUrl);
          return [];
        });
      candidateRequests.set(requestUrl, request);
    }

    return request;
  }

  /**
   * Cards that stay are left in place, so focus and an add still running in one of them survive.
   * @param {HTMLElement[]} cards
   */
  #show(cards) {
    const { list } = this.refs;
    const current = new Map(
      Array.from(list.children, (card) => [card instanceof HTMLElement ? card.dataset.productId : '', card])
    );
    const next = cards.map((card) => current.get(card.dataset.productId) ?? document.importNode(card, true));
    const kept = new Set(next);

    for (const card of Array.from(list.children)) {
      if (!kept.has(card)) card.remove();
    }
    next.forEach((card, index) => {
      if (list.children[index] !== card) list.insertBefore(card, list.children[index] ?? null);
    });
  }
}

if (!customElements.get('fabini-product-cross-sell')) {
  customElements.define('fabini-product-cross-sell', FabiniProductCrossSell);
}

/**
 * Product page bundle tile (snippets/fabini-bundle-tile.liquid). The server leaves out bundles
 * already in the cart; after a cart change that adds or removes one of this product's bundles,
 * the tile is re-rendered from the product section so it switches between bundles and the
 * cross-sell without a reload.
 */
class FabiniBundleSlot extends HTMLElement {
  #refreshId = 0;

  connectedCallback() {
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  #handleCartUpdate = async () => {
    const bundleIds = (this.dataset.bundleIds ?? '').split(',').filter(Boolean);
    if (bundleIds.length === 0) return;

    const refreshId = ++this.#refreshId;
    const cartProductIds = await getCartProductIds();
    const inCart = bundleIds.filter((id) => cartProductIds.has(id));
    const rendered = (this.dataset.bundlesInCart ?? '').split(',').filter(Boolean);
    if (refreshId !== this.#refreshId || inCart.join(',') === rendered.join(',')) return;

    const section = this.closest('.shopify-section');
    const sectionId = section?.id.replace(/^shopify-section-/, '');
    if (!sectionId) return;

    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);

    try {
      const html = await fetch(url, { cache: 'no-store' }).then((response) => (response.ok ? response.text() : ''));
      const next = new DOMParser().parseFromString(html, 'text/html').querySelector('fabini-bundle-slot');
      if (refreshId !== this.#refreshId || !(next instanceof HTMLElement) || !this.isConnected) return;

      // The theme's morph updates children only; carry over the rendered cart state as well.
      morph(this, next);
      this.dataset.bundleIds = next.dataset.bundleIds ?? '';
      this.dataset.bundlesInCart = next.dataset.bundlesInCart ?? '';
    } catch (error) {
      console.error(error);
    }
  };
}

if (!customElements.get('fabini-bundle-slot')) {
  customElements.define('fabini-bundle-slot', FabiniBundleSlot);
}
